import {
  cacheKeys,
  Issue as DomainIssue,
  FeatureName,
  MediaType,
  Message,
  NewMessage,
  PickerMode,
  useCreateIssueMessage,
  useFindUnreadActivityInbox,
  useIsFeatureEnabled,
  useIssue,
  useOnboardingIssueView,
  useSetActivityInboxRead,
  useUpdateIssue,
} from '@gripp/shared-logic';
import {
  Colors,
  IssueContext,
  IssueFooter,
  IssueMessages,
  IssueMessagesRef,
  Layout,
  RoutineExecutionProps,
  RoutineModal,
  SlideAction,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { activityPaths } from '../../activity/routes';
import { assetPaths } from '../../assets/assetUtils';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { getMediaBaseUrl } from '../../shared/mediaUtils';
import { useCurrentIssueContext } from '../issueContext';
import { Issue as IssueOld } from './issue_old';
import { IssueHeaderCropCircle } from './issueHeaderCropCircle';

type IssueProps = {
  isComingFromActivities?: boolean;
};

export const Issue = (props: IssueProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (!isCropCircleRedesignEnabled) {
    return <IssueOld {...props} />;
  }

  return <IssueBase {...props} />;
};

export const IssueBase = (props: IssueProps): JSX.Element => {
  const { onSlideChange, setColumnTitle } = useSlideLayout();
  const { setCurrentIssue } = useCurrentIssueContext() || {};
  const { issueId, workspaceId } = useParams();
  const messagesRef = useRef<IssueMessagesRef>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { issue, isLoading } = useIssue(issueId ?? '');

  // Automatically advance onboarding when viewing an onboarding issue
  useOnboardingIssueView(issue);

  const [messagesCount, setMessagesCount] = useState<number | undefined>(
    undefined
  );
  const [showFooter, setShowFooter] = useState(true);
  const { mutate: updateIssue } = useUpdateIssue();
  const { createIssueMessage } = useCreateIssueMessage();
  const { t } = useTranslation();
  const [routineModalVisible, setRoutineModalVisible] = useState(false);
  const [routineModalProps, setRoutineModalProps] = useState<
    RoutineExecutionProps | undefined
  >();
  const queryClient = useQueryClient();
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onBeforePrint: async () => {
      setShowFooter(false);
      setIsPrinting(true);
      await new Promise((resolve) => setTimeout(resolve, 1));
    },
    onAfterPrint: () => {
      setShowFooter(true);
      setIsPrinting(false);
    },
    // pageStyle: '@page { margin: 10px; }',    // setting this causes images to not show up
    documentTitle: (issue as any)?.title,
  });
  const [isPrinting, setIsPrinting] = useState(false);
  const [issueKey, setIssueKey] = useState(0);

  const onPrint = async () => {
    handlePrint();
  };

  useEffect(() => {
    setMessagesCount(undefined);
  }, [issueId]);

  useEffect(() => {
    if (issue?.title) {
      setColumnTitle(issue.title);
    }
  }, [setColumnTitle, issue]);

  const onIssueDelete = () => {
    if (history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/activities');
    }
  };

  const { data: inboxData } = useFindUnreadActivityInbox();

  const { setAsRead } = useSetActivityInboxRead();

  useEffect(() => {
    if (issue && inboxData) {
      setAsRead(issue.id, inboxData.items, true);
    }
  }, [issue, inboxData]);

  const viewAssetInfo = props.isComingFromActivities
    ? async () => {
        await onSlideChange(SlideAction.NEXT);

        navigate(`/assets/${issue.asset.id}/detail`);
      }
    : undefined;

  const onIssueHeaderChanged = (severity: string) => {
    const input: any = {
      id: issueId,
      severity: severity,
    };

    // If Note (purple) changes to Report severity (red/yellow/green), convert to Report
    if (
      issue.type === 'NOTE' &&
      issue.severity === 'purple' &&
      ['red', 'yellow', 'green'].includes(severity)
    ) {
      input.type = 'REPORT';
    }

    updateIssue(
      { input: input },
      {
        onSettled: () => {
          const updated = {
            ...issue,
            ...input,
            updatedAt: new Date().toISOString(),
          };
          setCurrentIssue && setCurrentIssue(updated);
        },
      }
    );
  };

  const onNewMessage = async (message: NewMessage) => {
    await createIssueMessage({
      issue: issue,
      message: message,
      options: {
        onSettled: async (data: Message) => {
          const newMessage = data;
          messagesRef.current?.addNewMessage(newMessage);

          await queryClient.invalidateQueries({
            queryKey: cacheKeys('issue').all,
          });
        },
      },
    });
  };

  const onRoutineSelected = async (props: RoutineExecutionProps) => {
    setRoutineModalProps(props);
    setRoutineModalVisible(true);
  };

  const onRoutineCanceled = async () => {
    setRoutineModalVisible(false);

    if (routineModalProps?.onCanceled) {
      routineModalProps.onCanceled();
    }
  };

  const onRoutineCompleted = async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('issuemessage').all,
    });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('workspaceasset').all,
    });
    setIssueKey((prev) => prev + 1);
    setRoutineModalVisible(false);

    if (routineModalProps?.onCompleted) {
      routineModalProps.onCompleted();
    }
  };

  const onRouteToIssue = async (issueId: string) => {
    if (props.isComingFromActivities) {
      navigate(`/${activityPaths.root}/${issueId}`);
    } else {
      navigate(
        `/${assetPaths.root}/${(issue as any).asset.id}/activities/${issueId}`
      );
    }
  };

  const onBackPress = props.isComingFromActivities
    ? async () => {
        if (location.pathname === `/activities/${issueId}/edit`) {
          await onSlideChange(SlideAction.PREV);
          navigate(`/activities/${issueId}`);
          return;
        }

        if (location.pathname.startsWith(`/activities/${issueId}/asset/`)) {
          await onSlideChange(SlideAction.PREV);
          navigate(`/activities/${issueId}`);
          return;
        }

        await onSlideChange(SlideAction.SINGLE);
        navigate(`/${activityPaths.root}`, {
          state: { skipAutoSelect: true },
        });
      }
    : undefined;

  const onAssetPress = (issue as any)?.asset
    ? async () => {
        if (props.isComingFromActivities) {
          navigate(`/activities/${issueId}/asset/${(issue as any).asset.id}`);
        } else if (workspaceId) {
          navigate(`/workspaces/${workspaceId}`);
        } else {
          await onSlideChange(SlideAction.NEXT);
          navigate(`/assets/${(issue as any).asset.id}/detail`);
        }
      }
    : undefined;

  return (
    !isLoading &&
    issue && (
      <IssueContext.Provider value={{ onRoutineSelected, onRouteToIssue }}>
        <View style={[styles.width100, styles.flex1]} ref={printRef}>
          <IssueHeaderCropCircle
            issue={issue}
            onChanged={onIssueHeaderChanged}
            setShowFooter={setShowFooter}
            titleStyle={styles.issueTitle}
            viewAssetInfo={viewAssetInfo}
            onIssueDelete={onIssueDelete}
            onPrint={onPrint}
            onBackPress={onBackPress}
            onAssetPress={onAssetPress}
            assetButtonLabel={
              workspaceId ? t('workspace.titleSingular') : undefined
            }
          />
          <View style={[styles.issueContainer, styles.flex1]}>
            <View style={styles.messages}>
              <IssueMessages
                key={issueKey}
                ref={messagesRef}
                issue={issue as DomainIssue}
                mediaUrl={getMediaBaseUrl()}
                inbox={inboxData?.items}
                onMessagesCountChange={setMessagesCount}
                setAsRead={setAsRead}
              />
            </View>
            {showFooter && (
              <IssueFooter
                onNewMessage={onNewMessage}
                mediaPicker={MediaPicker}
                workspaceId={issue?.workspace?.id}
                mode={PickerMode.ATTACHMENT}
                containerStyle={styles.footer}
                acceptedMediaTypes={[
                  MediaType.Image,
                  MediaType.Document,
                  MediaType.Video,
                ]}
              />
            )}
          </View>
        </View>
        {routineModalProps && (
          <RoutineModal
            {...routineModalProps}
            visible={routineModalVisible}
            onCanceled={onRoutineCanceled}
            onCompleted={onRoutineCompleted}
          />
        )}
      </IssueContext.Provider>
    )
  );
};

const styles = StyleSheet.create({
  issueContainer: {
    ...Layout.container,
    borderRadius: 4,
    overflow: 'hidden',
    minHeight: 0,
    backgroundColor: Colors.brand.white,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  messages: {
    flex: 1,
    minHeight: 0,
  },
  issueTitle: {
    paddingLeft: 15,
  },
  footer: {
    backgroundColor: Colors.brand.white,
  },
  print: {
    flex: 1,
    margin: 10,
  },
  width100: {
    width: '100%',
  },
  flex1: {
    flex: 1,
    minHeight: 0,
  },
});
